const TOKEN_KEY = "gtm_intel_token";
const USER_KEY = "gtm_intel_user";
const API_BASE = import.meta.env.VITE_GTM_INTEL_API_URL ?? "/api/intel";

export type GTMUserRole = "admin" | "leadership" | "ic";

export interface StoredUser {
  email: string;
  gtm_role: GTMUserRole;
  firstName?: string | null;
  lastName?: string | null;
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function getStoredUser(): StoredUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
}

function storeUser(user: StoredUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/** Exchange a Supabase access_token (from OAuth or magic link callback) for a dashboard JWT. */
export async function exchangeSupabaseToken(
  accessToken: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/auth/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ access_token: accessToken }),
    });
    if (!res.ok) {
      const data = (await res.json()) as { error?: string };
      return { success: false, error: data.error ?? "Token exchange failed" };
    }
    const data = (await res.json()) as {
      token: string;
      email: string;
      gtm_role: GTMUserRole;
      firstName?: string | null;
      lastName?: string | null;
    };
    storeToken(data.token);
    storeUser({
      email: data.email,
      gtm_role: data.gtm_role,
      firstName: data.firstName,
      lastName: data.lastName,
    });
    return { success: true };
  } catch {
    return { success: false, error: "Network error during token exchange" };
  }
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function authHeaders(): Record<string, string> {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function getApiBase(): string {
  return API_BASE;
}

export async function login(
  email: string,
  password: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/auth/token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const data = (await res.json()) as { error?: string };
      return { success: false, error: data.error ?? "Login failed" };
    }
    const data = (await res.json()) as {
      token: string;
      email: string;
      gtm_role: GTMUserRole;
      firstName?: string | null;
      lastName?: string | null;
    };
    storeToken(data.token);
    storeUser({
      email: data.email,
      gtm_role: data.gtm_role,
      firstName: data.firstName,
      lastName: data.lastName,
    });
    return { success: true };
  } catch {
    return { success: false, error: "Network error — is GTM Intel running?" };
  }
}

/**
 * Returns true if the session is valid, false if it is definitively invalid
 * (token missing or 4xx response). On transient backend errors (5xx / network),
 * retries once and returns true to avoid a spurious redirect to login — the
 * token is kept so the next real API call will surface the real state.
 */
export async function verifySession(): Promise<boolean> {
  const token = getStoredToken();
  if (!token) return false;

  const attempt = async (): Promise<Response | null> => {
    try {
      return await fetch(`${API_BASE}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      return null;
    }
  };

  let res = await attempt();

  // Transient error (network failure or 5xx) — retry once before giving up.
  if (res === null || res.status >= 500) {
    await new Promise((r) => setTimeout(r, 800));
    res = await attempt();
  }

  // Still transient after retry — assume session may still be valid.
  // Don't clear the token; let the user stay on the page.
  if (res === null || res.status >= 500) {
    return true;
  }

  // Definitive 4xx: session is invalid, clear the token.
  if (!res.ok) {
    clearToken();
    return false;
  }

  const data = (await res.json()) as { valid: boolean; email?: string; gtm_role?: GTMUserRole };
  if (!data.valid) {
    clearToken();
    return false;
  }
  // Refresh stored user info from the latest token payload
  if (data.email && data.gtm_role && !getStoredUser()) {
    storeUser({ email: data.email, gtm_role: data.gtm_role });
  }
  return true;
}

export function isAdmin(user: StoredUser | null): boolean {
  return user?.gtm_role === "admin";
}

export function logout(): void {
  clearToken();
  window.location.href = "/login";
}

export async function loginWithGoogle(): Promise<{ success: boolean; error?: string }> {
  try {
    // Send the current origin so the OAuth callback returns to the host the
    // user signed in from (crm.axiomgtm.com vs the apex vs *-staging). Without
    // it the server falls back to APP_URL, which bounces every host to the
    // canonical one — found live 2026-07-17 (crm.axiomgtm.com → axiomgtm.com).
    // The server validates the shape and Supabase's redirect allow-list remains
    // the security gate; unlisted origins are rejected there.
    const res = await fetch(`${API_BASE}/auth/oauth/google`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ origin: window.location.origin }),
    });
    if (!res.ok) {
      const data = (await res.json()) as { error?: string };
      return { success: false, error: data.error ?? "Google sign-in unavailable" };
    }
    const data = (await res.json()) as { redirect_url?: string };
    if (data.redirect_url) {
      window.location.href = data.redirect_url;
    }
    return { success: true };
  } catch {
    return { success: false, error: "Google sign-in unavailable — check provider configuration" };
  }
}

export async function sendMagicLink(email: string): Promise<{ success: boolean; error?: string }> {
  if (!email.trim()) return { success: false, error: "Email is required" };
  try {
    const res = await fetch(`${API_BASE}/auth/magic-link`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.trim() }),
    });
    if (!res.ok) {
      const data = (await res.json()) as { error?: string };
      return { success: false, error: data.error ?? "Magic link unavailable" };
    }
    return { success: true };
  } catch {
    return { success: false, error: "Magic link unavailable — check provider configuration" };
  }
}
